import { UseQueryOptions } from '@tanstack/react-query';
import { useProfileVisitedEvents } from 'hooks/events/useOpenProfileEvents';
import { useContactListFeatureQuery } from 'hooks/queries/useContactListFeatureQuery';
import { useTruckerProfileQuery } from 'hooks/queries/useTruckerProfileQuery';
import { useOpenSearchContext } from 'hooks/useOpenSearchContext';
import { useTruckerProps } from 'hooks/useTruckerProps';
import { ITruckerProfileProps } from 'interfaces/profile.interface';
import { useEffect, useMemo } from 'react';
import { IFeatureFlagrResponse } from 'services/featureFlagr';
import { isEmpty } from 'utils/validate';

interface FetchProfileQUeriesProps {
  truckerProfileOptions?: Partial<UseQueryOptions<ITruckerProfileProps>>;
  contactListFeatureOptions?: Partial<UseQueryOptions<IFeatureFlagrResponse>>;
}

const useFetchProfileQueries = ({
  contactListFeatureOptions,
  truckerProfileOptions,
}: FetchProfileQUeriesProps = {}) => {
  const { truckerId, freightId, dispatchEventOnStart, eventsSource } = useTruckerProps();

  const {
    isLoading: isLoadingTruckerData,
    isFetching: isFetchingTruckerData,
    isError: isErrorTruckerData,
    isLoadingError: isLoadingErrorTruckerData,
    isRefetchError: isRefetchErrorTruckerData,
    isFetched: isFetchedTruckerData,
    refetch: refetchTruckerData,
    data: truckerData,
    isSuccess,
  } = useTruckerProfileQuery(truckerProfileOptions);

  const { isLastSearchedDrivers } = useOpenSearchContext();
  const { profileVisitedEvents } = useProfileVisitedEvents();

  useEffect(() => {
    if (isSuccess && dispatchEventOnStart) {
      profileVisitedEvents({
        codeRegion: Array.from(new Set(truckerData?.trucker?.phones?.map((phone) => phone.ddd))),
        isVerified: truckerData?.isVerified,
        source: eventsSource,
        truckerId,
        freightId,
        latestConsultations: isLastSearchedDrivers ? 'lastconsultation' : 'normalconsultation',
      });
    }
  }, [truckerData, isSuccess, isFetchedTruckerData]);

  const {
    isLoading: isLoadingContactListFeature,
    isFetching: isFetchingContactListFeature,
    isError: isErrorContactListFeature,
    isLoadingError: isLoadingErrorContactListFeature,
    isRefetchError: isRefetchErrorContactListFeature,
    isFetched: isFetchedContactListFeature,
    refetch: refetchContactListFeature,
    data: contactListData,
  } = useContactListFeatureQuery(contactListFeatureOptions);

  const isLoading = useMemo(
    () => isLoadingTruckerData || isFetchingTruckerData || isLoadingContactListFeature || isFetchingContactListFeature,
    [isLoadingTruckerData, isFetchingTruckerData, isLoadingContactListFeature, isFetchingContactListFeature]
  );

  const isError = useMemo(
    () =>
      (isErrorTruckerData ||
        isLoadingErrorTruckerData ||
        isRefetchErrorTruckerData ||
        isErrorContactListFeature ||
        isLoadingErrorContactListFeature ||
        isRefetchErrorContactListFeature) &&
      !isLoading,
    [
      isErrorTruckerData,
      isLoadingErrorTruckerData,
      isRefetchErrorTruckerData,
      isErrorContactListFeature,
      isLoadingErrorContactListFeature,
      isRefetchErrorContactListFeature,
      isLoading,
    ]
  );

  const isFetched = useMemo(
    () =>
      (isFetchedTruckerData || !isEmpty(truckerData)) &&
      (isFetchedContactListFeature || !isEmpty(contactListData)) &&
      !isError &&
      !isLoading,
    [isFetchedTruckerData, isFetchedContactListFeature, isError, isLoading, truckerData]
  );

  const refetch = () => {
    /* istanbul ignore else */
    if (isErrorTruckerData || isLoadingErrorTruckerData || isRefetchErrorTruckerData) {
      refetchTruckerData();
    }

    /* istanbul ignore else */
    if (isErrorContactListFeature || isLoadingErrorContactListFeature || isRefetchErrorContactListFeature) {
      refetchContactListFeature();
    }
  };

  return { isLoading, isError, isFetched, refetch, truckerData, isSuccess };
};

export { useFetchProfileQueries };
